import type {
  GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One,
  GetApiV1ThreeLetterCodePartnerAdminsUsers200,
  PartnerAdminsBankAccount,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { convertDateTime } from '~/utils/dateTime';
import type { UserListBankAccountPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof UserListBankAccountPresentation>;

type Args = {
  /** API から取得した利用者一覧データ */
  usersData: GetApiV1ThreeLetterCodePartnerAdminsUsers200;
  /**
   * API から取得した口座情報一覧データ。
   * userIds が空のとき（Empty 状態）は依存フェッチが発火せず undefined になる。
   * undefined の場合は全行 '-' を表示する。
   */
  bankAccountsData: GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One | undefined;
  /**
   * 検索条件が適用されているか
   *
   * true のとき 0 件でも Empty 画面を表示しない（PEN-309 対応パターン）
   */
  isFiltered: boolean;
};

type IConvertToPresentationProps = (params: Args) => {
  rows: PresentationProps['rows'];
  /** 利用者が 0 件かどうか */
  isEmpty: boolean;
};

/**
 * 同一 user_id のレコードが複数ある場合に linked_at が最新の1件を返す。
 *
 * @param accounts - 同一 user_id の口座情報レコード群
 * @returns linked_at が最大（最新）のレコード
 */
const pickLatestBankAccount = (accounts: PartnerAdminsBankAccount[]): PartnerAdminsBankAccount =>
  accounts.reduce((latest, current) => (current.linked_at > latest.linked_at ? current : latest));

/**
 * API 型（snake_case）を UserListBankAccount Presentation の props 型（camelCase）に変換する。
 *
 * - 出力型は `UserListBankAccountPresentation` の Props から導出し、型乖離を防ぐ
 * - isEmpty フラグを導出して Container 層での表示出し分けに使用する
 * - 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応パターン）
 * - bankAccountsData と usersData を user_id で突き合わせて口座情報列を実データ表示する
 * - 同一 user_id のレコードが複数の場合は linked_at が最新の1件を採用する
 * - 口座未連携ユーザーは '-' 表示
 * - bankAccountsData が undefined（依存フェッチ未発火）の場合は全行 '-' 表示
 * - billable_from（利用開始日）が null の場合は '-' を表示し、設定済みの場合は 'yyyy/M/d' フォーマット
 * - updated_at（更新日時）は unix タイムスタンプ（秒）から 'yyyy/M/d H:mm' フォーマット
 * - ページネーション（pager）の組み立ては Container 層（generatePagerProps）が担う
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  usersData,
  bankAccountsData,
  isFiltered,
}) => {
  const users = usersData.users;
  const bankAccounts = bankAccountsData?.bank_accounts ?? [];

  // user_id → 最新口座情報 のマップを構築する
  const bankAccountByUserId = new Map<number, PartnerAdminsBankAccount>();
  for (const account of bankAccounts) {
    const existing = bankAccountByUserId.get(account.user_id);
    if (!existing) {
      bankAccountByUserId.set(account.user_id, account);
    } else {
      bankAccountByUserId.set(account.user_id, pickLatestBankAccount([existing, account]));
    }
  }

  const rows: PresentationProps['rows'] = users.map(user => {
    const bankAccount = bankAccountByUserId.get(user.id);

    const billableFrom = user.billable_from
      ? formatDate({
          date: new Date(`${user.billable_from}T00:00:00`),
          formatType: 'yyyy/M/d',
        })
      : '-';

    const updatedAt = formatDate({
      date: new Date(user.updated_at * 1000),
      formatType: 'yyyy/M/d H:mm',
    });

    return {
      id: user.id,
      clientName: user.client.name,
      name: user.full_name,
      email: user.email ?? '',
      branchCode: bankAccount?.branch_number ?? '-',
      accountNumber: bankAccount ? `••••••••${bankAccount.account_number_last4}` : '-',
      linkedAt: bankAccount ? convertDateTime(bankAccount.linked_at) : '-',
      createdAt: convertDateTime(user.created_at),
      billableFrom,
      updatedAt,
    };
  });

  // 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応パターン）
  const isEmpty = users.length === 0 && !isFiltered;

  return { rows, isEmpty };
};
